import { useCallback, useEffect, useRef, useState } from "react";
import { BMI_ERROR_MESSAGE, ONLY_NUMBERS } from "@/constants";
import { calculateBMI, sanitizeDecimal } from "@/utils";
import { CSSTransition } from "react-transition-group";
import { Button, ButtonType } from "@/components/common/button";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import {
  DEFAULT_RANGE,
  getWeightTooHighError,
  getWeightTooLowError,
} from "@/utils/validateField/weight";
import { MICRODOSE_MIN_BMI_THRESHOLD } from "@/constants/bmi";
import { useIsMicrodose } from "@/hooks/useIsMicrodose";
import { MicrodoseDisclaimer } from "@/pages/quiz/base/components/microdoseDisclaimer";

export type HeightWeightAnswer = {
  feet: string;
  inches: string;
  weight: string;
};

type Validators = Record<InputKey, (value: string) => string>;

enum InputKey {
  FEET = "feet",
  WEIGHT = "weight",
  INCHES = "inches",
}

const generalValidator = (min: number, max: number) => (value: string) => {
  if (Number(value) < min) {
    return String(min);
  }
  if (Number(value) > max) {
    return String(max);
  }
  return value;
};

const validators: Validators = {
  [InputKey.FEET]: generalValidator(3, 8),
  [InputKey.WEIGHT]: generalValidator(DEFAULT_RANGE.min, DEFAULT_RANGE.max),
  [InputKey.INCHES]: generalValidator(0, 11),
};

const BodyMeasurementInput = ({
  label,
  unit,
  value,
  onChange,
  onBlur,
  onFocus,
  inputMode = "tel",
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: (v: string) => void;
  onFocus?: (v: string) => void;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}) => {
  return (
    <div className="w-full flex-1">
      <label className="text-sm mb-2">{label}</label>
      <div className="relative flex items-center">
        <input
          value={value}
          onChange={({ target }) => onChange(target.value)}
          onBlur={({ target }) => onBlur && onBlur(target.value)}
          onFocus={({ target }) => onFocus && onFocus(target.value)}
          type="text"
          className="p-2 sm:p-3 text-lg sm:text-xl border-[3px] w-full border-soft rounded-lg focus:border-medium-green focus:outline-none"
          inputMode={inputMode}
        />
        {unit && (
          <div className="absolute right-1 pr-5 pl-2 py-2 bg-white">{unit}</div>
        )}
      </div>
    </div>
  );
};

const NUMBER_OF_ANSWERS = 3;

const heightWeightInputsWithBMI = ({
  question,
  questionValue,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<HeightWeightAnswer>) => {
  const [errorMessage, setErrorMessage] = useState("");
  const { isMicrodose } = useIsMicrodose();
  const errorMessageRef = useRef(null);
  const { skipBmiValidation } = question.attributes;

  const isAllInputsFilled =
    Object.values(questionValue).filter(Boolean).length === NUMBER_OF_ANSWERS;

  useEffect(() => {
    // reset error on any input
    setErrorMessage("");
  }, [questionValue]);

  const { weight, feet, inches } = questionValue;
  const currentBMI =
    weight && feet && inches
      ? calculateBMI(+weight, +feet, +inches).toFixed(2)
      : 0;

  const isWeightInRange =
    Number(weight) >= DEFAULT_RANGE.min && Number(weight) <= DEFAULT_RANGE.max;
  const showMicrodoseDisclaimer = isAllInputsFilled && isWeightInRange && isMicrodose;

  const onSubmitAnswer = () => {
    const { weight, feet, inches } = questionValue;
    // Validate the same 2-decimal BMI the user sees, otherwise e.g. 21.997
    // is shown as 22.00 but rejected as below the threshold.
    const bmi = Number(calculateBMI(+weight, +feet, +inches).toFixed(2));

    if (Number(weight) < DEFAULT_RANGE.min) {
      setErrorMessage(getWeightTooLowError(DEFAULT_RANGE.min));
      return;
    }

    if (Number(weight) > DEFAULT_RANGE.max) {
      setErrorMessage(getWeightTooHighError(DEFAULT_RANGE.max));
      return;
    }

    if (!skipBmiValidation && bmi < MICRODOSE_MIN_BMI_THRESHOLD) {
      setErrorMessage(BMI_ERROR_MESSAGE);
      return;
    }

    setErrorMessage("");

    setQuestionAnswer(questionValue);
  };

  const handleWeightChange = useCallback(
    (inputValue: string) => {
      let cleanedInput = sanitizeDecimal(inputValue);
      setQuestionValue({ ...questionValue, weight: cleanedInput });
    },
    [questionValue]
  );

  const handleInputChange = useCallback(
    (inputValue: string, inputKey: string) => {
      let cleanedInput = inputValue.replace(ONLY_NUMBERS, "");
      setQuestionValue({ ...questionValue, [inputKey]: cleanedInput });
    },
    [questionValue]
  );

  const handleBlur = useCallback(
    (inputValue: string, inputKey: string) => {
      if (inputValue === "") {
        return;
      }
      let cleanedInput = Number(inputValue).toString();
      cleanedInput = validators[inputKey as InputKey](cleanedInput);
      setQuestionValue({ ...questionValue, [inputKey]: cleanedInput });
    },
    [questionValue]
  );

  return (
    <>
      <div className="mb-2 flex w-full flex-col gap-4">
        <p className="quiz-description text-center text-sm font-medium">
          Your BMI
          <p className="text-4xl font-bold ">{currentBMI || "0.0"}</p>
        </p>
        <input
          value={currentBMI}
          type="range"
          min="0"
          max="40"
          step="1"
          className="pointer-events-none h-4 w-full appearance-none rounded-full bg-gradient-to-r from-dark-purple-2 to-soft accent-white outline-none"
        />
      </div>
      <div className="mb-2 w-full flex justify-between items-center gap-2.5 sm:gap-3 lg:gap-4">
        <BodyMeasurementInput
          label="Feet"
          unit="Ft."
          value={questionValue["feet"]}
          onChange={(v: string) => handleInputChange(v, InputKey.FEET)}
          onBlur={(v) => handleBlur(v, InputKey.FEET)}
        />

        <BodyMeasurementInput
          label="Inches"
          unit="In."
          value={questionValue["inches"]}
          onChange={(v: string) => handleInputChange(v, InputKey.INCHES)}
          onBlur={(v) => handleBlur(v, InputKey.INCHES)}
        />
      </div>

      <BodyMeasurementInput
        label="Current weight (lbs.)"
        unit="Lbs."
        value={questionValue["weight"]}
        onChange={(v: string) => handleWeightChange(v)}
        inputMode="decimal"
      />

      {showMicrodoseDisclaimer && <MicrodoseDisclaimer />}

      <CSSTransition
        nodeRef={errorMessageRef}
        in={!!errorMessage}
        timeout={300}
        classNames="fade"
        unmountOnExit
      >
        <div
          ref={errorMessageRef}
          className="mt-4 text-brick-red font-medium text-sm sm:text-base"
        >
          {errorMessage}
        </div>
      </CSSTransition>

      <Button
        type={ButtonType.NEXT}
        disabled={!isAllInputsFilled || !!errorMessage}
        onClick={onSubmitAnswer}
      />
    </>
  );
};

export { heightWeightInputsWithBMI };
